/**
 * "Something changed" signal for one household. Production uses Supabase Realtime
 * (postgres_changes on sync_events); the dev backend offers a long-poll with the same meaning.
 * The signal only triggers a pull — data always comes from the change log.
 */
type RealtimeSubscription = { close(): void };

export function subscribeDevChanges(options: {
  baseUrl: string;
  householdId: string;
  getAccessToken: () => Promise<string | null>;
  onChange: () => void;
}): RealtimeSubscription {
  let closed = false;
  let known = 0;
  let controller: AbortController | null = null;

  const loop = async () => {
    let failures = 0;
    while (!closed) {
      try {
        const token = await options.getAccessToken();
        if (!token) {
          await new Promise((r) => setTimeout(r, 5000));
          continue;
        }
        controller = new AbortController();
        const response = await fetch(
          `${options.baseUrl}/dev-realtime/v1/wait?householdId=${encodeURIComponent(options.householdId)}&seq=${known}`,
          { headers: { authorization: `Bearer ${token}` }, signal: controller.signal },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const { seq } = (await response.json()) as { seq: number };
        failures = 0;
        if (seq > known) {
          const first = known === 0;
          known = seq;
          if (!first) options.onChange();
        }
      } catch {
        if (closed) return;
        failures += 1;
        await new Promise((r) => setTimeout(r, Math.min(30_000, 1000 * 2 ** failures)));
      }
    }
  };
  void loop();
  return {
    close() {
      closed = true;
      controller?.abort();
    },
  };
}
